/**
 * The exact set of `tx`-namespaced i18n message keys the SDK's trading
 * helpers fire through the injected `TxTranslator` (`deps.t(...)`).
 *
 * SINGLE SOURCE OF TRUTH for those keys. It exists because the keys cross a
 * package boundary: the SDK fires them, but the host owns the `en.json` they
 * resolve against and the i18n lint that guards completeness. Before this
 * contract the keys were hand-mirrored in three places — the SDK call site,
 * the host `en.json`, and the host unused-key allowlist — with no enforcement,
 * so a new key compiled fine and only crashed at runtime with `MISSING_MESSAGE`.
 *
 * Two enforcement points hang off this list:
 *  1. `TxTranslator`'s call signature is narrowed to `TxMessageKey`, so a new
 *     or mistyped key is a COMPILE error in this package until registered here.
 *  2. The host derives its i18n required-keys + unused-allowlist from
 *     `TX_MESSAGE_KEY_PATHS` (imported via `@morfi/backend-sdk-v2/trading-keys`),
 *     so `en.json` completeness is checked at the consumer's precommit.
 *
 * Keys are stored WITHOUT the `tx.` namespace prefix — that is the form the
 * scoped translator is called with (`deps.t('toast.shareTrade')`). The
 * `tx.`-prefixed full paths (what `en.json` actually contains) are derived in
 * `TX_MESSAGE_KEY_PATHS`.
 *
 * Error keys (`errors.*`) are intentionally NOT here. They are dynamic (driven
 * by on-chain error codes) and resolved through `resolveErrorMessage`, which
 * guards with `t.has(...)` and falls back to the raw key — a missing error key
 * degrades gracefully instead of throwing, so it needs no static contract.
 */

export const TX_MESSAGE_KEYS = [
  'toast.submittingDirection',
  'toast.submittingDirectionRetry',
  'toast.directionConfirmed',
  'toast.confirmed',
  'toast.claimAddedToWallet',
  'toast.boostConfirmed',
  'toast.submittingBoost',
  'toast.signing',
  'toast.submittingThesis',
  'toast.thesisConfirmed',
  'toast.thesisFailed',
  'toast.failedPrefix',
  'toast.cancelled',
  'toast.cancelledDescription',
  'toast.cancelButton',
  'toast.closeButton',
  'toast.retryWithSlippage',
  'toast.addThesis',
  'toast.shareTrade',
  'toast.viewExplorer',
  'share.modalTitle',
  'share.tweetText',
  'share.tweetTextSellPnl',
  'recovery.tryAgain',
  'recovery.retryWithSlippage',
  'recovery.retryingIn',
  'recovery.checkPortfolio',
] as const;

export type TxMessageKey = (typeof TX_MESSAGE_KEYS)[number];

/** The `tx.`-namespaced full i18n paths — what the host `en.json` must contain. */
export const TX_MESSAGE_KEY_PATHS: readonly string[] = TX_MESSAGE_KEYS.map((key) => `tx.${key}`);
